import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  AutoComplete,
  Radio,
  TreeSelect,
  Button,
  Collapse,
  message,
  Segmented,
  Tag,
  Tooltip,
  Switch,
  Alert,
  DatePicker,
  Space,
} from 'antd';
import {
  getPredictionUpdateTaskTypeId,
  getSceneTree,
  getSceneInspectionList,
  resolveSceneIdsByNames as resolveSceneIdsByNamesApi,
  getSceneGroupStats,
  createJob,
  createBazelJob,
  getJobBazelPresets,
  getDailyTagsExisting,
  getDailyVersionsByTag,
  getPredictionDataVersionCreateOptions,
  createSchedule,
  previewCron,
  getPriorityList,
  BazelPresets,
  SceneTreeNode,
  SceneGroupStat,
  BuildType,
  BuildSpec,
  CreateJobParams,
  CreateBazelJobParams,
  DailyVersionItem,
  JobItem,
  ScheduleDelayUnit,
  PriorityOption,
  PredictionDataVersionCreateOption,
} from '@apis/simulation';
import {
  encodeGroupBackendToken, sceneGroupDisplay,
} from '@/constants/sceneGroup';
import dayjs, { type Dayjs } from 'dayjs';
import { usePackageExistsProbe } from '../CreateJobModal/usePackageExistsProbe';
import { renderProbeHint, renderProbeSuffix } from '../CreateJobModal/probeIndicators';
import DailyBaseVersionPicker, {
  DAILY_TAG_HINT_EVENT,
} from '../CreateJobModal/DailyBaseVersionPicker';
import CustomParametersFields from '../CreateJobModal/CustomParametersFields';
import ModelReleaseField from '../CreateJobModal/ModelReleaseField';
import { useCustomParameterDefinitions } from '../CreateJobModal/useCustomParameterDefinitions';
import {
  splitModelReleaseParams, mergeModelReleaseParams, PROMOTED_CUSTOM_PARAMETER_KEYS,
} from '@/utils/jobCustomParameters';
import { extractPredictionDataVersion } from '@/utils/versionTag';

// ── 数据更新（PR_DATA）专属创建 Job 弹窗 ──
// 本文件从 PNC 通用 CreateJobModal 整体复制而来（独立副本，不复用组件），
// 唯一差异：任务类型**固定为 PR_DATA**——不给用户下拉选择，挂载时经
// getPredictionUpdateTaskTypeId() 拿隐藏 taskTypeId，UI 只读展示「数据更新 (PR_DATA)」。
// 其余（创建类型 / daily / 场景来源 / 定时排程 / 高级选项）与 PNC 完全一致。
//
// 任务类型固定 = PR_DATA（数据更新子链路），前端不可切换。
const PR_DATA_TASK_TYPE_NAME = 'PR_DATA';
const PR_DATA_TASK_TYPE_LABEL = '数据更新 (PR_DATA)';

// 前端 UI 类型 3 项:
// - precompiled(合并原「导包」+「版本号」)→ 后端 package_neodata
// - package_bazel → 后端 package_bazel(手工输入 baseVersion + branchDpInfo)
// - daily → 后端 package_bazel(基于 daily 历史打版选一个 baseVersion,快捷方式)
// compile_platform / cmake 编译已废弃,不再在 UI 出现;后端 CompileBuildSpec 保留
// 供老 Job 详情回读。详见 docs/daily-tag-and-build-option-refactor.md §4.1。
type UiBuildType = 'precompiled' | 'package_bazel' | 'daily';

// 默认选中「使用编译好的版本」
const DEFAULT_BUILD_TYPE: UiBuildType = 'precompiled';
const BUILD_TYPE_LABELS: Record<UiBuildType, ReactNode> = {
  precompiled: '使用编译好的版本',
  package_bazel: '基于已有版本编译',
  daily: (
    <>
      基于 daily 版本编译<strong>(推荐)</strong>
    </>
  ),
};
const BUILD_TYPE_TOOLTIPS: Record<UiBuildType, string> = {
  precompiled: '直接使用已发布的算法包版本号或百度云 S3 包地址，无需重新编译。',
  package_bazel: '基于一个已有的基线版本 + 分支/commit 组合，走 cicd 平台重新编译一份产物。',
  daily: '选择一个 daily 通道 tag(如 release.10.11 / orin_dev),从近期 daily 打版历史里挑一个基线版本重跑;提交后与「基于已有版本编译」走同一 bazel 编译链路。',
};
const BUILD_TYPE_ORDER: UiBuildType[] = [
  'precompiled',
  'daily',
  'package_bazel',
];

// precompiled 子选项卡：
// - version:直接输版本号(短串,走后端模板)
// - s3:bos:// 完整路径透传
// - daily:选一个 daily tag → 从近期打版历史里挑一个 versionNo 回填,再走 version 分支
//   (即最终仍以 algorithmVersion 字段提交,走 package_neodata;与 buildType='daily'
//   分支的区别:那个是拉基线后走 bazel 重编,这个是直接拿 daily 那天已经打好的 neodata 包)
type PrecompiledSubtype = 'version' | 's3' | 'daily';
const PRECOMPILED_SUBTYPE_LABELS: Record<PrecompiledSubtype, string> = {
  version: '版本号',
  s3: 'S3 地址',
  daily: '使用 daily 版本',
};

// ── bazel 一键填版本预设 ──
// 预设 JSON 由后端 ``/job/bazel-presets`` 返回（管理员在「系统 - 系统参数运维」页
// 编辑）；前端挂载时拉取，不再硬编码。结构类型 BazelPreset / BazelPresets 在
// @apis/simulation 统一定义，本文件只 import BazelPresets 用。

// ── 预测数据版本解析 ──
// 版本号解析（含 AD 字母基线）统一收敛到 `@/utils/versionTag`，与后端
// `version_tag.extract_prediction_data_version` 同口径。

// 「按场景 id 发起 job」单次最多携带的场景数（与后端 MAX_SCENE_IDS_PER_JOB 一致）。
export const MAX_SCENE_IDS_PER_JOB = 100;

// job.job_name 列为 varchar(64)，复制命名需与后端上限保持一致
const MAX_JOB_NAME_LEN = 64;

/**
 * 生成「复制」后的 Job 名称：把末尾连续的 `_copy` / `_copyN` 归一成单个带序号后缀，
 * 并截断 base 段保证不超过 {@link MAX_JOB_NAME_LEN}。
 */
function buildCopyName(name: string): string {
  if (!name) return '';
  const m = name.match(/^(.*?)((?:_copy\d*)+)$/);
  const base = m ? m[1] : name;
  let next = 1;
  if (m) {
    const last = m[2].match(/_copy(\d*)$/);
    next = last && last[1] ? parseInt(last[1], 10) + 1 : 2;
  }
  const suffix = next <= 1 ? '_copy' : `_copy${next}`;
  const maxBase = Math.max(0, MAX_JOB_NAME_LEN - suffix.length);
  const trimmedBase = base.length > maxBase ? base.slice(0, maxBase) : base;
  return `${trimmedBase}${suffix}`;
}

/** UI 端 module/branch 分隔符：兼容空格（首选）与冒号（兼容老格式）。 */
const BRANCH_DP_PAIR_SEP_RE = /[:\s]+/;
/** UI 端多对之间的分隔符：换行（首选）或英文逗号。 */
const BRANCH_DP_GROUP_SEP_RE = /[,\n]+/;

/** 把单行 ``module<sep>branch`` 拆成 ``[module, branch]``；不符合格式返回 null。 */
function parseBranchDpEntry(line: string): [string, string] | null {
  const trimmed = (line || '').trim();
  if (!trimmed) return null;
  const m = trimmed.split(BRANCH_DP_PAIR_SEP_RE);
  if (m.length < 2) return null;
  const module = m[0]?.trim();
  const branch = m.slice(1).join(' ').trim();
  if (!module || !branch) return null;
  return [module, branch];
}

/** 把 UI 多行输入归一化为后端接口契约的 ``module:branch,module2:branch2``。 */
function normalizeBranchDpInfoForApi(raw: string): string {
  return (raw || '')
    .split(BRANCH_DP_GROUP_SEP_RE)
    .map(parseBranchDpEntry)
    .filter((p): p is [string, string] => p !== null)
    .map(([module, branch]) => `${module}:${branch}`)
    .join(',');
}

/** 从 UI 输入提取模块名列表（用于 changeModule 自动填充）。 */
function deriveChangeModule(branchDpInfo: string): string {
  const modules: string[] = [];
  for (const entry of (branchDpInfo || '').split(BRANCH_DP_GROUP_SEP_RE)) {
    const pair = parseBranchDpEntry(entry);
    const mod = pair?.[0];
    if (mod && !modules.includes(mod)) {
      modules.push(mod);
    }
  }
  return modules.join(',');
}

/**
 * 合并「当前已填分支」与「一键填版本预设分支」，输出 ``module:branch,...`` 串。
 *
 * 规则（保序）：以当前已填条目为基准并保持其顺序，预设里**同名仓库覆盖**其分支、
 * **新仓库追加**到末尾；当前已填但预设未涉及的仓库**原样保留**。current 为空时结果
 * 等于预设本身（回退到旧「直接覆盖」行为）。
 */
function mergeBranchDpInfo(current: string, presetBranchDpInfo: string): string {
  const entries: [string, string][] = [];
  const posByModule = new Map<string, number>();
  const upsert = (raw: string) => {
    for (const line of (raw || '').split(BRANCH_DP_GROUP_SEP_RE)) {
      const pair = parseBranchDpEntry(line);
      if (!pair) continue;
      const [module, branch] = pair;
      const at = posByModule.get(module);
      if (at !== undefined) {
        entries[at] = [module, branch]; // 覆盖（同名去重 / 预设覆盖已填）
      } else {
        posByModule.set(module, entries.length);
        entries.push([module, branch]);
      }
    }
  };
  upsert(current);              // 先铺当前已填，确立顺序
  upsert(presetBranchDpInfo);   // 预设覆盖同名、追加新仓库
  // 用换行拼接、仓库与分支用空格间隔：UI 上一个仓库一行更易读
  // （parseBranchDpEntry 兼容空格/冒号，提交时 normalizeBranchDpInfoForApi 再统一成逗号串）。
  return entries.map(([m, b]) => `${m} ${b}`).join('\n');
}

const DEFAULT_BRANCH = 'orin_dev';

/** buildCommitIdString 的逆操作：把 ``repo:val,repo:val`` 还原为表单 textarea 文本。 */
function commitIdToTextarea(commitId: string | null | undefined): string {
  if (!commitId) return '';
  const lines: string[] = [];
  for (const pair of commitId.split(',')) {
    const [repo, value] = pair.split(':').map(x => x.trim());
    if (repo && value && value !== DEFAULT_BRANCH) {
      lines.push(`${repo} ${value}`);
    }
  }
  return lines.join('\n');
}

interface CopyDefaults {
  algorithmVersion: string;
  /** 后端 snapshot 后的完整包路径（package_neodata 模板拼好 / bos:// 透传）。
   *  Radio 手动切到「导包」时取这个完整 URL 填 s3PackageUrl。 */
  fullPackageUrl: string;
  branchCommitInput: string;
  baseVersion?: string;
  versionDescription?: string;
  changeModule?: string;
  branchDpInfo?: string;
}

export interface PresetScene {
  id: number;
  name: string;
}

export interface CreatePredictionUpdateJobModalProps {
  open: boolean;
  onClose: () => void;
  /** 创建成功后回调（如刷新列表）。 */
  onSuccess?: () => void;
  /**
   * 场景管理「发起任务」预选场景：调用方已持有 ``{id, name}``，弹窗内直接预填到
   * 「按场景」页签，**不再走 id→name 反查接口**。提交时使用 id 直发，上限
   * {@link MAX_SCENE_IDS_PER_JOB}。
   */
  presetScenes?: PresetScene[];
  /**
   * 「按任务查询」入口:JobTaskList 页 FilteredStatRow 的「基于当前查询发起任务」
   * 按钮 → confirm 弹窗 → 打开时传入。场景池锁定为该 Job 里满足 filters 组合的
   * distinct scene_id。用户在弹窗内**不可**再改 filter,只能配置版本/参数。
   *
   * 无该 prop 时,Segmented 里不显示「按任务查询」页签。
   */
  byTaskQuery?: {
    jobId: number;
    /** TaskListParams 里除 jobId/current/pageSize 外的全部 filter,可空对象。 */
    filters: Record<string, unknown>;
    /** 「按选中任务发起」:勾选的行对应 scene_id。非空时后端跳过 filters 反查,
     *  直接用这份场景池建匿名场景集(手动点选入口)。 */
    sceneIds?: number[];
    /** 已格式化的 filter 展示项(中文名 + 中文值),给「按任务查询」页签只读渲染。 */
    filterEntries?: Array<{ key: string; label: string; valueLabel: string }>;
    /** 只读展示用:该 Job 的名称(可选,能显示更友好)。 */
    jobName?: string;
    /** 只读展示用:场景池大小(distinct scene_id 数)。 */
    sceneCount?: number;
  };
  /**
   * 场景管理「勾选整个集合发起任务」：调用方传入 scene_tree 节点 id 数组（叶子或目录），
   * 或分组虚拟节点编码字符串 ``L<leafId>G:<groupKey>``（daily 分组勾选场景）。
   */
  presetSceneSetIds?: (number | string)[];
  /** 分组虚拟节点显示名映射 ``{"L91G:detour_problem": "daily / 绕行问题"}``。 */
  presetSceneSetGroupLabels?: Record<string, string>;
  /** 复制 job：传入原 record，弹窗打开时自动回填发起参数。 */
  copyRecord?: JobItem | null;
  /**
   * dry-run 模式:不真调 create_job / create_bazel_job,只把弹窗组装出的 payload
   * 通过 ``onDryRunResult`` 回调返给上层。
   */
  dryRunMode?: boolean;
  versionPlaceholder?: string;
  onDryRunResult?: (payload: {
    endpoint: 'create' | 'bazel_create';
    params: Record<string, unknown>;
  }) => void;
}

/** 「基于 daily 版本」radio 下的 Tag 下拉联动区。
 *
 *  交互:
 *  - Tag 下拉:拉 GET /job/daily-tags-existing 拿实际使用过的 tag(倒序按最近使用)
 *  - 选完 Tag → GET /job/daily/versions?tag=&limit=200 拉该 tag 下近 200 条 daily job
 *  - **自动回填**最新一条 versionNo 到父表单的「基线版本」;把候选历史列表通过
 *    onVersionsLoaded 上抛给父层,供下方「基线版本」Picker 的历史弹窗做搜索/选择
 *
 *  详见 docs/daily-tag-and-build-option-refactor.md §4.1 / §4.5。
 */
function DailyTagPicker(props: {
  onPick: (baseVersion: string) => void;
  onVersionsLoaded: (versions: DailyVersionItem[]) => void;
  onTagChange: (tag: string | undefined) => void;
}) {
  const { onPick, onVersionsLoaded, onTagChange } = props;
  const [tags, setTags] = useState<string[]>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);
  const [versionLoading, setVersionLoading] = useState(false);
  // 用户点了未激活的「历史」按钮时,Picker 广播事件让本组件把 Select 高亮一下。
  const [hintTick, setHintTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setTagLoading(true);
    getDailyTagsExisting()
      .then((list) => {
        if (!cancelled) setTags(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTagLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 监听 DailyBaseVersionPicker 广播的引导事件:未选 tag 时点了「历史」,本组件把 Select 高亮 ~1.2s。
  useEffect(() => {
    const onHint = () => setHintTick((n) => n + 1);
    window.addEventListener(DAILY_TAG_HINT_EVENT, onHint);
    return () => window.removeEventListener(DAILY_TAG_HINT_EVENT, onHint);
  }, []);

  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTag(tag);
    onTagChange(tag);
    setVersionLoading(true);
    getDailyVersionsByTag(tag, 200)
      .then((rows) => {
        // stale-guard:请求发出后用户可能 clear / 换 tag,响应回来时该丢弃。
        setSelectedTag((current) => {
          if (current !== tag) return current;
          onVersionsLoaded(rows);
          const latest = rows.find((r) => !!r.versionNo);
          if (latest?.versionNo) {
            onPick(latest.versionNo);
          }
          return current;
        });
      })
      .catch(() => {
        onVersionsLoaded([]);
      })
      .finally(() => {
        setVersionLoading(false);
      });
  }, [onPick, onVersionsLoaded, onTagChange]);

  return (
    <Form.Item
      label={
        <span
          className={hintTick > 0 ? 'daily-tag-label-hint' : undefined}
          key={hintTick}
        >
          Daily Tag
        </span>
      }
      tooltip="选择一个 daily 通道 tag(如 release.10.11 / orin_dev),后端会自动把该 tag 下最新一条 daily 打版的版本号(versionNo)回填到下方「基线版本」;同时下方输入框变成可搜索,能从近 200 条历史 versionNo 里挑其它版本。"
      extra={
        versionLoading
          ? <span style={{ color: '#9b9da3', fontSize: 12 }}>加载 tag 下历史版本中...</span>
          : selectedTag
            ? <span style={{ color: '#9b9da3', fontSize: 12 }}>
                已回填该 tag 下最新 daily 版本号;下方「基线版本」支持搜索历史或手动输入
              </span>
            : undefined
      }
    >
      <span
        className={hintTick > 0 ? 'daily-tag-select-pulse' : undefined}
        // key 换掉让 CSS 动画重跑;单次触发靠一次性 remount。
        key={hintTick}
        style={{ display: 'inline-block', borderRadius: 6 }}
      >
        <Select
          value={selectedTag}
          loading={tagLoading}
          placeholder="选择 daily 通道 tag"
          showSearch
          allowClear
          style={{ width: 240 }}
          options={tags.map((t) => ({ label: t, value: t }))}
          onSelect={(v) => handleTagSelect(v as string)}
          // onChange 兜底:allowClear × / 键盘清空等所有路径都会触发 onChange(undefined)。
          onChange={(v) => {
            if (v === undefined || v === null || v === '') {
              setSelectedTag(undefined);
              onVersionsLoaded([]);
              onTagChange(undefined);
            }
          }}
        />
      </span>

      {/* 引导动画:CSS-only。ring 从内向外扩散,同时 label 短暂变蓝。 */}
      <style>{`
        @keyframes daily-tag-hint-ring {
          0%   { box-shadow: 0 0 0 0 rgba(22, 119, 255, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(22, 119, 255, 0); }
          100% { box-shadow: 0 0 0 0 rgba(22, 119, 255, 0); }
        }
        @keyframes daily-tag-hint-label {
          0%   { color: #1677ff; }
          70%  { color: #1677ff; }
          100% { color: inherit; }
        }
        .daily-tag-select-pulse {
          animation: daily-tag-hint-ring 1.2s ease-out 2;
        }
        .daily-tag-label-hint {
          animation: daily-tag-hint-label 2.4s ease-out 1;
          font-weight: 600;
        }
      `}</style>
    </Form.Item>
  );
}

/**
 * 创建「数据更新（PR_DATA）」Job 弹窗。
 *
 * 从 PNC 通用 CreateJobModal 整体复制的独立副本，唯一差异是任务类型固定为
 * PR_DATA（前端不可切换，隐藏 taskTypeId 由 getPredictionUpdateTaskTypeId 提供）。
 * 场景来源 UI 一致：「按场景集合」TreeSelect 与「按场景」输入场景名 二选一页签。
 */
export default function CreatePredictionUpdateJobModal({
  open,
  onClose,
  onSuccess,
  presetScenes,
  byTaskQuery,
  presetSceneSetIds,
  presetSceneSetGroupLabels,
  copyRecord = null,
  dryRunMode = false,
  versionPlaceholder = '{version}',
  onDryRunResult,
}: CreatePredictionUpdateJobModalProps) {
  const [createForm] = Form.useForm();
  // 任务类型固定 PR_DATA：挂载时反查隐藏 taskTypeId（seed 未跑时为 null，提交前兜底提示）。
  const [prDataTaskTypeId, setPrDataTaskTypeId] = useState<number | null>(null);
  // 自定义参数目录的唯一真源：顶层「模型主版本」字段与「高级选项 · 自定义参数」块共用，
  // 避免两处各自拉一次、对同一份目录给出互相矛盾的判断（详见该 hook 注释）。
  const customParameterDefinitions = useCustomParameterDefinitions(open);
  // PR_DATA 的 task_type 中文名（如"数据更新"），用作默认 jobName 前缀；
  // seed 未跑或接口失败时为 null，前缀回退硬编码 code 常量。
  const [prDataTaskTypeName, setPrDataTaskTypeName] = useState<string | null>(null);
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);
  /**
   * daily 子树叶子节点的 scene_group 分组统计（弹窗打开时懒加载一次）：
   * `{ [leafId]: [{sceneGroup, count}] }`。
   */
  const [dailyGroupStats, setDailyGroupStats] = useState<Record<number, SceneGroupStat[]>>({});
  const [createLoading, setCreateLoading] = useState(false);
  // 高级选项面板展开态（受控）：自定义参数校验失败时自动展开，复制含参 Job 时默认展开。
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [buildType, setBuildType] = useState<UiBuildType>(DEFAULT_BUILD_TYPE);
  // precompiled 内部子选项卡：version=版本号 / s3=S3 地址；两条都映射到后端 package_neodata。
  const [precompiledSubtype, setPrecompiledSubtype] = useState<PrecompiledSubtype>('version');
  // bazel 一键填版本 下拉当前选择：'custom' 表示自定义（不自动填），其它值为预设 key。
  const [bazelPresetChoice, setBazelPresetChoice] = useState<'custom' | string>('custom');
  // bazel 预设字典：挂载时从 /job/bazel-presets 拉取；拉失败返回 {}，下拉只剩「自定义版本」。
  const [bazelPresets, setBazelPresets] = useState<BazelPresets>({});
  // daily 分支下 Tag 选完后拿到的历史 daily 版本候选(最多 200 条,createTime DESC)。
  const [dailyBaseVersionCandidates, setDailyBaseVersionCandidates] = useState<DailyVersionItem[]>([]);
  // 当前是否选中 Daily Tag(权威信号)。
  const [dailyTagSelected, setDailyTagSelected] = useState(false);
  // precompiled 分支的 daily subtype 独立持有 candidates/tagSelected。
  const [precompiledDailyCandidates, setPrecompiledDailyCandidates] = useState<DailyVersionItem[]>([]);
  const [precompiledDailyTagSelected, setPrecompiledDailyTagSelected] = useState(false);
  // 「预测数据版本」下拉候选：数据更新页「预测数据版本」列表 + 最近真实产出过的版本
  // （GET /prediction-update/data-versions/create-options）。值恒为**完整版本串**
  // （prediction.AD3.1.3）：选已存在的 = 补进该版本，手填新串 = 以该串新建版本；
  // 是否已存在由后端 hasHeader 判定，不看前端猜测。
  const [fillVersionOptions, setFillVersionOptions] = useState<
    PredictionDataVersionCreateOption[]
  >([]);
  const [fillVersionLoading, setFillVersionLoading] = useState(false);
  // 命中已存在版本时「已有场景」的处理策略：skip（默认，保留旧数据只补缺）/ overwrite。
  const [fillMode, setFillMode] = useState<'skip' | 'overwrite'>('skip');
  // 高级选项 · 优先级下拉候选。挂载时从 /job/priority 拉一次。
  const [priorityList, setPriorityList] = useState<PriorityOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    getPriorityList()
      .then((list) => {
        if (!cancelled) setPriorityList(list);
      })
      .catch((err) => {
        console.error('加载优先级列表失败:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    getJobBazelPresets()
      .then((presets) => {
        if (!cancelled) setBazelPresets(presets);
      })
      .catch((e) => {
        // 拉取失败按"不兜底"决策：下拉只剩自定义版本，侦测跳转静默禁用
        console.error('load bazel presets failed:', e);
      });
    return () => { cancelled = true; };
  }, []);

  // ── bazel 编译预设：一键填，复用同一份给「算法包版本侦测自动跳过来」 ──
  const applyBazelPreset = useCallback((key: string, carryBaseVersion?: string) => {
    const preset = bazelPresets[key];
    if (!preset) {
      message.warning(`预设 ${key} 不存在或已下线，请重新选择`);
      return;
    }
    const carry = (carryBaseVersion || '').trim();
    const effectiveBaseVersion = carry || preset.baseVersion || '';
    // 合并而非覆盖：保留用户已填的仓库分支，预设仅覆盖同名仓库、补充新仓库。
    const currentBranchDpInfo = String(createForm.getFieldValue('branchDpInfo') || '');
    const mergedBranchDpInfo = mergeBranchDpInfo(currentBranchDpInfo, preset.branchDpInfo);
    const hadExisting = deriveChangeModule(currentBranchDpInfo).length > 0;
    const patch: Record<string, unknown> = {
      branchDpInfo: mergedBranchDpInfo,
      changeModule: deriveChangeModule(mergedBranchDpInfo),
    };
    if (effectiveBaseVersion) {
      patch.baseVersion = effectiveBaseVersion;
    }
    createForm.setFieldsValue(patch);
    const mergeHint = hadExisting ? '（已合并你已填的分支，同名仓库以预设为准）' : '';
    message.success(
      carry
        ? `已填入 ${preset.label} 预设分支并带入算法版本到基线${mergeHint}`
        : `已填入 ${preset.label} 预设分支与默认基线版本${mergeHint}`,
    );
  }, [createForm, bazelPresets]);

  const [copyDefaults, setCopyDefaults] = useState<CopyDefaults | null>(null);
  // 场景来源页签:'sceneSet'=按场景集;'scene'=按场景名输入;'byJob'=按任务查询
  const [sceneTab, setSceneTab] = useState<'sceneSet' | 'scene' | 'byJob'>('sceneSet');
  // 复制「按场景」job 时带入的原始场景 id。
  const [copiedSceneIds, setCopiedSceneIds] = useState<number[] | null>(null);

  // ── 定时/延迟/循环创建 Job 排程（docs/schedule-v3.md §5.1） ──
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<'scheduled' | 'delayed' | 'cron'>('scheduled');
  const [cronPreview, setCronPreview] = useState<string[] | null>(null);
  const [cronPreviewLoading, setCronPreviewLoading] = useState(false);

  // 场景管理「发起任务」带入的场景列表（id+name 稳定串作依赖）
  const presetSceneKey = (presetScenes || []).map((s) => `${s.id}:${s.name}`).join('|');
  const presetScenesRef = useRef(presetScenes);
  presetScenesRef.current = presetScenes;
  const presetSceneSetIdsKey = (presetSceneSetIds || []).map(String).join(',');
  const presetSceneSetIdsRef = useRef(presetSceneSetIds);
  presetSceneSetIdsRef.current = presetSceneSetIds;
  /** daily 分组虚拟节点编码 token（``L<leafId>G:<groupKey>``）暂存。 */
  const [stagedGroupTokens, setStagedGroupTokens] = useState<string[]>([]);

  // 「使用编译好的版本 → 版本号 / S3 地址」和「基于已有版本编译 → 基线版本」三处
  // 都是"往 BOS 探一次"语义。
  const algoVersionValue = Form.useWatch('algorithmVersion', createForm) as string | undefined;
  const baseVersionValue = Form.useWatch('baseVersion', createForm) as string | undefined;
  const s3PackageUrlValue = Form.useWatch('s3PackageUrl', createForm) as string | undefined;
  // 预测数据版本（必填，完整串 prediction.AD3.1.3）：随「版本号 / 基线版本」自动提取，
  // 用户可手改。自动提取的最近一次值：用户手动改成别的值后不再被覆盖。
  const autoPredictionVersionRef = useRef<string>('');
  const algoVersionProbe = usePackageExistsProbe(
    algoVersionValue,
    'package_neodata',
    buildType === 'precompiled' && precompiledSubtype === 'version',
  );
  const baseVersionProbe = usePackageExistsProbe(
    baseVersionValue,
    'package_bazel',
    buildType === 'package_bazel' || buildType === 'daily',
  );
  const s3PackageUrlProbe = usePackageExistsProbe(
    s3PackageUrlValue,
    'package_neodata',
    buildType === 'precompiled'
      && precompiledSubtype === 's3'
      && /^bos:\/\//i.test((s3PackageUrlValue ?? '').trim()),
  );

  // 版本来源（dataVersion 自动提取用）随创建类型切换：
  // - precompiled(版本号/daily) → algorithmVersion；precompiled(S3) → s3PackageUrl
  // - package_bazel / daily     → baseVersion（基线版本）
  const tagSourceValue =
    buildType === 'precompiled'
      ? (precompiledSubtype === 's3' ? s3PackageUrlValue : algoVersionValue)
      : baseVersionValue;
  // 预测数据版本自动提取：来源（版本号 / 基线版本 / S3 地址）一变就按后端同口径解析，
  // 回填**完整版本串** prediction.{dv}（orin_dev → prediction.orin_dev，时间戳由后端追加）；
  // 用户手改成别的值后不再覆盖，解析不出（返回 null）时保留用户输入并交由必填校验提示。
  const lastDataVersionSourceRef = useRef<string>('');
  useEffect(() => {
    const source = (tagSourceValue || '').trim();
    if (source === lastDataVersionSourceRef.current) return;
    lastDataVersionSourceRef.current = source;
    const extracted = extractPredictionDataVersion(source);
    if (!extracted) return;
    const full = `prediction.${extracted}`;
    const current = String(createForm.getFieldValue('predictionVersion') || '').trim();
    if (current && current !== autoPredictionVersionRef.current) return;
    if (current !== full) {
      autoPredictionVersionRef.current = full;
      createForm.setFieldsValue({ predictionVersion: full });
    }
  }, [tagSourceValue, createForm]);

  // 当前输入（完整串）与命中元信息：决定状态提示 + 是否展示「已有场景」策略。
  const predictionVersionValue = Form.useWatch('predictionVersion', createForm) as string | undefined;
  const trimmedPredictionVersion = String(predictionVersionValue || '').trim();
  const selectedVersionMeta = trimmedPredictionVersion
    ? fillVersionOptions.find((v) => v.predictionVersion === trimmedPredictionVersion)
    : undefined;
  const versionExists = Boolean(selectedVersionMeta?.hasHeader);
  // 切到「不存在」的版本（新建）时策略无意义，复位为默认 skip。
  useEffect(() => {
    if (!versionExists) setFillMode('skip');
  }, [versionExists, trimmedPredictionVersion]);

  // 用户手填短版本号（AD3.1.3 / orin_dev）时，失焦自动补全 prediction. 前缀——
  // 对外只暴露完整串一个形态，避免用户困惑「为什么还要拼前缀」。
  const normalizePredictionVersionInput = useCallback(() => {
    const raw = String(createForm.getFieldValue('predictionVersion') || '').trim();
    if (!raw || raw.startsWith('prediction.')) return;
    createForm.setFieldsValue({ predictionVersion: `prediction.${raw}` });
  }, [createForm]);

  // 「预测数据版本」候选：目录版本 + 最近真实产出过的版本（含覆盖数 / 是否当前）。
  // 拉不到（老环境 / 无数据）只是没有候选，仍可手填新建。
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFillVersionLoading(true);
    getPredictionDataVersionCreateOptions()
      .then((list) => {
        if (cancelled) return;
        setFillVersionOptions(list);
      })
      .catch((err) => console.error('加载预测数据版本候选失败:', err))
      .finally(() => {
        if (!cancelled) setFillVersionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // 加载固定任务类型 PR_DATA 的隐藏 id 并写入表单。
  const loadPrDataTaskType = useCallback(async () => {
    try {
      const { id, name } = await getPredictionUpdateTaskTypeId();
      setPrDataTaskTypeId(id);
      setPrDataTaskTypeName(name);
      if (id != null) {
        createForm.setFieldsValue({ taskTypeId: id });
      }
    } catch (err) {
      console.error('加载 PR_DATA 任务类型失败:', err);
    }
  }, [createForm]);

  // 「按场景集合」页签需要场景树 + daily 子树的分组统计
  const loadSceneTree = useCallback(async () => {
    try {
      const tree = await getSceneTree(true);
      setSceneTree(tree);
      // daily 子树叶子（node_flag=1）拉一次分组统计，用来挂虚拟子节点
      const dailyRoot = tree.find(n => n.name === 'daily' && n.parentId === -1);
      if (dailyRoot) {
        const dailyLeafIds: number[] = [];
        const walk = (n: SceneTreeNode) => {
          if (n.nodeFlag === 1) dailyLeafIds.push(n.id);
          n.children?.forEach(walk);
        };
        walk(dailyRoot);
        if (dailyLeafIds.length) {
          const entries = await Promise.all(dailyLeafIds.map(async (id) => {
            try {
              const stats = await getSceneGroupStats([id]);
              return [id, stats] as const;
            } catch {
              return [id, []] as const;
            }
          }));
          setDailyGroupStats(Object.fromEntries(entries));
        }
      }
    } catch (err) {
      console.error('加载场景树失败:', err);
    }
  }, []);

  // 按场景 id 预填「按场景」页签（复制 job 用）
  const prefillSceneTabByIds = useCallback(async (ids: number[]) => {
    setSceneTab('scene');
    setCopiedSceneIds(ids.length > 0 ? ids : null);
    let sceneNames: string[] = [];
    if (ids.length > 0) {
      try {
        const res = await getSceneInspectionList({ ids, current: 1, pageSize: ids.length });
        sceneNames = (res.list || []).map((s) => s.name).filter(Boolean);
      } catch (err) {
        console.error('场景 id 反查场景名失败:', err);
        message.warning('场景名解析失败，请手动确认场景名');
      }
    }
    createForm.setFieldsValue({ sceneNames });
  }, [createForm]);

  // 场景管理「发起任务」预填：调用方已带 id+name，直接填，不调接口。
  const prefillSceneTabByScenes = useCallback((scenes: PresetScene[]) => {
    setSceneTab('scene');
    setCopiedSceneIds(scenes.length > 0 ? scenes.map((s) => s.id) : null);
    createForm.setFieldsValue({
      sceneNames: scenes.map((s) => s.name).filter(Boolean),
    });
  }, [createForm]);

  // 把原 job 的发起参数回填进表单（复制 job）。
  const applyCopyRecord = useCallback(async (record: JobItem) => {
    let packageVersion = '';
    let commitInput = '';
    const bz = record.bazelInputs || {};
    const baseVersion = (bz.base_version || '').trim();
    const versionDescription = bz.version_description ?? 'test';
    const changeModule = bz.change_module || '';
    const branchDpInfo = (bz.branch_dp_info || '')
      .split(',')
      .map((entry: string) => entry.trim())
      .filter(Boolean)
      .map((entry: string) => entry.replace(/:/, ' '))
      .join('\n');

    const fullPackageUrl = (record.algorithmVersion || '').trim();
    if (record.buildType === 'compile_platform') {
      packageVersion = fullPackageUrl;
      if (record.buildSpec?.type === 'compile_platform') {
        commitInput = commitIdToTextarea(record.buildSpec.commitId);
      }
    } else {
      packageVersion =
        (record.buildSpec?.type === 'package_neodata' || record.buildSpec?.type === 'package_bazel') &&
        record.buildSpec.algorithmVersion
          ? record.buildSpec.algorithmVersion
          : fullPackageUrl;
    }

    setCopyDefaults({
      algorithmVersion: packageVersion,
      fullPackageUrl,
      branchCommitInput: commitInput,
      baseVersion,
      versionDescription,
      changeModule,
      branchDpInfo,
    });
    let initialUiType: UiBuildType;
    let initialPrecompiledSub: PrecompiledSubtype = 'version';
    if (record.buildType === 'package_bazel') {
      initialUiType = 'package_bazel';
    } else if (record.buildType === 'compile_platform') {
      initialUiType = 'precompiled';
    } else {
      initialUiType = 'precompiled';
      initialPrecompiledSub = /^bos:\/\//i.test(fullPackageUrl) ? 's3' : 'version';
    }
    setBuildType(initialUiType);
    setPrecompiledSubtype(initialPrecompiledSub);
    setBazelPresetChoice('custom');

    const isS3 = initialUiType === 'precompiled' && initialPrecompiledSub === 's3';
    // 数据更新固定任务类型：忽略源 Job 的 taskTypeId，仍用 PR_DATA 隐藏 id。
    // 模型主版本已提到顶层字段，回填时从 customParams 里拆出来单独写，其余留在自定义参数列表。
    const { modelRelease, entries: customParameterEntries } = splitModelReleaseParams(record.customParams);
    const baseFields = {
      priority: record.priority,
      customParameterEntries,
      modelRelease,
      jobName: record.jobName ? buildCopyName(record.jobName) : undefined,
      algorithmVersion: isS3 ? undefined : packageVersion,
      s3PackageUrl: isS3 ? fullPackageUrl : undefined,
      branchCommitInput: commitInput,
      baseVersion,
      versionDescription,
      changeModule,
      branchDpInfo,
    };

    const rawSceneTokens = (record.sceneSetIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (record.sceneSource === 'scene') {
      createForm.setFieldsValue(baseFields);
      const ids = rawSceneTokens.map(Number).filter((n) => !Number.isNaN(n));
      await prefillSceneTabByIds(ids);
    } else {
      setSceneTab('sceneSet');
      const sceneSetIds = rawSceneTokens
        .map(Number)
        .filter((n) => !Number.isNaN(n));
      createForm.setFieldsValue({ ...baseFields, sceneSetIds });
    }
  }, [createForm, prefillSceneTabByIds]);

  // 弹窗打开：重置 → 加载 PR_DATA 任务类型（非预选模式加载场景树）→ 复制回填
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCopyDefaults(null);
    // 复制含参 Job 时默认展开高级选项；模型主版本已移到上层，只有"其余自定义参数"非空才需要展开。
    setAdvancedOpen(splitModelReleaseParams(copyRecord?.customParams).entries.length > 0);
    setCopiedSceneIds(null);
    setBuildType(copyRecord?.buildType === 'package_bazel' ? 'package_bazel' : DEFAULT_BUILD_TYPE);
    setPrecompiledSubtype('version');
    setBazelPresetChoice('custom');
    setSceneTab('sceneSet');
    setEnableSchedule(false);
    setScheduleMode('scheduled');
    setCronPreview(null);
    // 预测数据版本：重开弹窗清掉「自动提取」记忆 + 覆盖策略，按本次输入重新提取。
    autoPredictionVersionRef.current = '';
    lastDataVersionSourceRef.current = '';
    setFillMode('skip');
    createForm.resetFields();
    (async () => {
      await Promise.all([loadPrDataTaskType(), loadSceneTree()]);
      if (cancelled) return;
      if (copyRecord) {
        await applyCopyRecord(copyRecord);
        if (byTaskQuery) setSceneTab('byJob');
      } else if (presetScenesRef.current && presetScenesRef.current.length > 0) {
        prefillSceneTabByScenes(presetScenesRef.current);
      } else if (presetSceneSetIdsRef.current && presetSceneSetIdsRef.current.length > 0) {
        const nums: number[] = [];
        const strs: string[] = [];
        for (const v of presetSceneSetIdsRef.current) {
          if (typeof v === 'number') nums.push(v);
          else if (typeof v === 'string' && v) strs.push(v);
        }
        setSceneTab('sceneSet');
        setStagedGroupTokens(strs);
        createForm.setFieldsValue({ sceneSetIds: nums });
      } else if (byTaskQuery) {
        setSceneTab('byJob');
      } else {
        setStagedGroupTokens([]);
      }
    })();
    return () => { cancelled = true; };
  }, [
    open, copyRecord, presetSceneKey, presetSceneSetIdsKey,
    byTaskQuery,
    loadPrDataTaskType, loadSceneTree, applyCopyRecord, prefillSceneTabByScenes, createForm,
  ]);

  // 弹窗打开时自动生成默认任务名（前缀用 task_type 中文名，如"数据更新"；
  // 接口未返回 name 时回退硬编码 code 常量；复制场景保留复制来的名称）
  useEffect(() => {
    if (open && prDataTaskTypeId != null && !copyDefaults && !createForm.getFieldValue('jobName')) {
      const now = new Date();
      const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
        '_',
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0'),
        String(now.getSeconds()).padStart(2, '0'),
        '_',
        String(now.getMilliseconds()).padStart(3, '0'),
      ].join('');
      const namePrefix = prDataTaskTypeName || PR_DATA_TASK_TYPE_NAME;
      createForm.setFieldsValue({ jobName: `${namePrefix}_${timestamp}` });
    }
  }, [open, prDataTaskTypeId, prDataTaskTypeName, createForm, copyDefaults]);

  // 将场景树转换为 TreeSelect 格式
  const convertSceneTreeToTreeData = (nodes: SceneTreeNode[]): any[] => {
    return nodes
      .filter((node) => !node.isHidden)
      .map((node) => {
        const realChildren = node.children && node.children.length > 0
          ? convertSceneTreeToTreeData(node.children)
          : undefined;

        // daily 子树叶子：把该 leaf 的 scene_group 分组挂成 TreeSelect 可选子节点
        let virtualGroupChildren: any[] | undefined;
        if (node.nodeFlag === 1 && dailyGroupStats[node.id]?.length) {
          virtualGroupChildren = dailyGroupStats[node.id].map((g) => ({
            title: sceneGroupDisplay(g.sceneGroup),
            value: encodeGroupBackendToken(node.id, g.sceneGroup),
            key: encodeGroupBackendToken(node.id, g.sceneGroup),
            isLeaf: true,
          }));
        }

        const children = realChildren || virtualGroupChildren
          ? [...(realChildren || []), ...(virtualGroupChildren || [])]
          : undefined;

        return {
          title: node.name,
          value: node.id,
          key: node.id,
          children,
        };
      });
  };

  const handleCancel = () => {
    setCopyDefaults(null);
    setEnableSchedule(false);
    setScheduleMode('scheduled');
    setCronPreview(null);
    createForm.resetFields();
    onClose();
  };

  /** 把用户输入的场景名精确反查为场景 id（「按场景·输入场景名」提交前调用）。 */
  const resolveSceneIdsByNames = (names: string[]): Promise<number[] | null> =>
    resolveSceneIdsByNamesApi(names);

  const handleSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      // 顶层「模型主版本」字段是唯一真源：并回 customParams，同时丢弃列表里可能残留的同名项。
      const customParams = mergeModelReleaseParams(values.customParameterEntries, values.modelRelease);

      // 固定 PR_DATA：seed 未跑时 taskTypeId 为空，直接拦截，避免提交无类型 Job。
      const effectiveTaskTypeId = prDataTaskTypeId ?? Number(values.taskTypeId);
      if (!effectiveTaskTypeId || Number.isNaN(effectiveTaskTypeId)) {
        message.error('PR_DATA 任务类型未就绪（seed 未初始化），请联系管理员');
        return;
      }

      setCreateLoading(true);

      // 计算场景来源载荷
      let sceneSourcePayload: {
        sceneIds?: number[];
        sceneSetIds?: string;
        byTaskQuery?: { jobId: number; filters: Record<string, unknown>; sceneIds?: number[] };
      };
      if (sceneTab === 'byJob') {
        if (!byTaskQuery) {
          message.error('缺少「按任务查询」参数,请回到 task 列表页重新发起');
          return;
        }
        sceneSourcePayload = {
          byTaskQuery: {
            jobId: byTaskQuery.jobId,
            filters: byTaskQuery.filters,
            ...(byTaskQuery.sceneIds && byTaskQuery.sceneIds.length > 0
              ? { sceneIds: byTaskQuery.sceneIds }
              : {}),
          },
        };
      } else if (sceneTab === 'scene') {
        if (copiedSceneIds && copiedSceneIds.length > 0) {
          if (copiedSceneIds.length > MAX_SCENE_IDS_PER_JOB) {
            message.error(`按场景发起最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${copiedSceneIds.length} 个`);
            return;
          }
          sceneSourcePayload = { sceneIds: copiedSceneIds };
        } else {
          const names: string[] = Array.from(
            new Set(
              ((values.sceneNames as string[]) || [])
                .map((n) => (n || '').trim())
                .filter(Boolean),
            ),
          );
          if (names.length === 0) {
            message.error('请输入场景名称');
            return;
          }
          if (names.length > MAX_SCENE_IDS_PER_JOB) {
            message.error(`按场景发起最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${names.length} 个`);
            return;
          }
          const resolvedIds = await resolveSceneIdsByNames(names);
          if (!resolvedIds) {
            return; // 反查失败 / 有不存在的场景名，错误已提示
          }
          sceneSourcePayload = { sceneIds: resolvedIds };
        }
      } else {
        const sceneSetIdsArray = Array.isArray(values.sceneSetIds)
          ? values.sceneSetIds.map((id: number | string) => String(id))
          : [];
        const merged = [...sceneSetIdsArray, ...stagedGroupTokens];
        if (merged.length === 0) {
          message.error('请至少选择一个场景集合');
          setCreateLoading(false);
          return;
        }
        sceneSourcePayload = { sceneSetIds: merged.join(',') };
      }

      // 高级选项：重复运行次数 1-10，默认 1。
      const repeatTimes = Math.max(1, Math.min(10, Number(values.repeatTimes) || 1));
      const repeatSuffix = repeatTimes > 1 ? `（共 ${repeatTimes} 个）` : '';

      // 预测数据版本（必填，**完整串** prediction.AD3.1.3）：validateFields 已保证非空。
      // 手填短版本号（AD3.1.3 / orin_dev）时这里补前缀——对外只暴露完整串一个形态。
      const rawPredictionVersion = String(values.predictionVersion || '').trim();
      if (!rawPredictionVersion) {
        message.error('请填写预测数据版本（如 prediction.AD3.1.3 / prediction.orin_dev）');
        setCreateLoading(false);
        return;
      }
      const predictionVersion = rawPredictionVersion.startsWith('prediction.')
        ? rawPredictionVersion
        : `prediction.${rawPredictionVersion}`;
      // 「短版本号」只作兼容通道（排程 / 重复运行）与目录元信息用；orin_dev 带时间戳时归一为 orin_dev。
      const shortDataVersion = (
        predictionVersion === 'prediction.orin_dev'
        || predictionVersion.startsWith('prediction.orin_dev.')
      )
        ? 'orin_dev'
        : predictionVersion.slice('prediction.'.length);
      if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(shortDataVersion)) {
        message.error('预测数据版本号只能包含字母 / 数字 / 点 / 下划线 / 中划线，且以字母或数字开头');
        setCreateLoading(false);
        return;
      }
      // 是否命中已存在版本：以候选列表的 hasHeader 为准（后端真实产出过才算存在）。
      const versionMeta = fillVersionOptions.find((v) => v.predictionVersion === predictionVersion);
      const versionExists = Boolean(versionMeta?.hasHeader);
      // 已有场景策略：仅对已存在版本生效；默认 skip（保留旧数据只补缺）。
      const effectiveFillMode = versionExists && fillMode === 'overwrite' ? 'overwrite' : undefined;

      // 命中已存在版本 = 补进该版本，与「重复运行」「定时排程」互斥（后端同样 400，这里前置给
      // 即时反馈）：重复运行时第 2..N 个 job 的场景行会撞上首批（同 scene_id + 同版本号）→ 空跑；
      // 排程化则到点才解析目标版本，目标被删/回收时是静默失败（fire-and-forget 只 log）。
      if (versionExists && repeatTimes > 1) {
        message.error('补进已存在版本不支持重复运行：产出只能写进同一个版本号，请把重复运行次数改为 1');
        setCreateLoading(false);
        return;
      }
      if (versionExists && enableSchedule) {
        message.error('补进已存在版本不支持定时/延迟排程：请改为立即发起，或换一个新版本号再建排程');
        setCreateLoading(false);
        return;
      }

      // 提交用版本字段：
      // - 立即发起：传完整串 predictionVersion（已存在 → 补跑；不存在 → 以该串新建），
      //   已存在且选了覆盖时附 fillMode=overwrite；
      // - 重复运行：后端禁止显式版本串，退回短 dataVersion（后端按 prediction.{dv} 拼）；
      // - 定时排程：payload 里同样不能带 predictionVersion，退短 dataVersion，到点再解析。
      const predictionVersionPayload: {
        dataVersion: string;
        predictionVersion?: string;
        fillMode?: 'skip' | 'overwrite';
      } = repeatTimes > 1
        ? { dataVersion: shortDataVersion }
        : {
            dataVersion: shortDataVersion,
            predictionVersion,
            ...(effectiveFillMode ? { fillMode: effectiveFillMode } : {}),
          };

      // ── 定时/延迟/循环创建 Job 排程 ──
      if (enableSchedule) {
        if (scheduleMode === 'scheduled') {
          const runAt = values.runAt as Dayjs | undefined;
          if (!runAt) {
            message.error('请选择定时触发时间');
            setCreateLoading(false);
            return;
          }
          if (runAt.toDate().getTime() < Date.now() + 60_000) {
            message.error('定时时间不得早于当前时间 + 1 分钟');
            setCreateLoading(false);
            return;
          }
        } else if (scheduleMode === 'delayed') {
          const dv = Number(values.delayValue);
          if (!Number.isFinite(dv) || dv < 1) {
            message.error('请输入有效的延时数值（≥1）');
            setCreateLoading(false);
            return;
          }
        } else {
          const expr = (values.cronExpr || '').trim();
          if (!expr) {
            message.error('请输入 cron 表达式');
            setCreateLoading(false);
            return;
          }
        }

        let jobEndpoint: 'create' | 'bazel_create';
        let payload: Record<string, any>;
        if (buildType === 'package_bazel' || buildType === 'daily') {
          const baseVersion = (values.baseVersion || '').trim();
          if (!baseVersion) {
            message.error('请输入基线版本');
            setCreateLoading(false);
            return;
          }
          jobEndpoint = 'bazel_create';
          payload = {
            taskTypeId: effectiveTaskTypeId,
            jobName: values.jobName || undefined,
            priority: values.priority !== undefined ? Number(values.priority) : undefined,
            ...sceneSourcePayload,
            baseVersion,
            versionDescription:
              (values.versionDescription || '').trim() || `${PR_DATA_TASK_TYPE_NAME}仿真`,
            changeModule: (values.changeModule || '').trim(),
            branchDpInfo: normalizeBranchDpInfoForApi(values.branchDpInfo || ''),
            // 排程 payload 不能带 predictionVersion（到点再解析），退回短 dataVersion。
            dataVersion: shortDataVersion,
            ...(customParams ? { customParams } : {}),
            ...(repeatTimes > 1 ? { repeatTimes } : {}),
            ...(buildType === 'package_bazel' && bazelPresetChoice !== 'custom'
              ? { presetKey: bazelPresetChoice } : {}),
          } satisfies CreateBazelJobParams;
        } else {
          jobEndpoint = 'create';
          let resolvedBuildType: BuildType;
          let buildSpec: BuildSpec;
          if (buildType === 'precompiled') {
            if (precompiledSubtype === 's3') {
              const bosUrl = (values.s3PackageUrl || '').trim();
              if (!/^(bos|obs):/i.test(bosUrl)) {
                message.error('S3 地址必须以 bos:// 或 obs:// 开头');
                setCreateLoading(false);
                return;
              }
              resolvedBuildType = 'package_neodata';
              buildSpec = { type: 'package_neodata', algorithmVersion: bosUrl };
            } else {
              const ver = (values.algorithmVersion || '').trim();
              if (!ver) {
                message.error('请输入版本号');
                setCreateLoading(false);
                return;
              }
              resolvedBuildType = 'package_neodata';
              buildSpec = { type: 'package_neodata', algorithmVersion: ver };
            }
          } else {
            message.error(`未知的 buildType=${String(buildType)},请刷新页面重试`);
            setCreateLoading(false);
            return;
          }
          payload = {
            priority: Number(values.priority),
            taskTypeId: effectiveTaskTypeId,
            jobName: values.jobName || undefined,
            ...sceneSourcePayload,
            buildType: resolvedBuildType,
            buildSpec,
            // 排程 payload 不能带 predictionVersion（到点再解析），退回短 dataVersion。
            dataVersion: shortDataVersion,
            ...(customParams ? { customParams } : {}),
            ...(repeatTimes > 1 ? { repeatTimes } : {}),
          } satisfies CreateJobParams;
        }

        const scheduleBody: import('@apis/simulation').ScheduleCreatePayload = {
          name: (values.jobName || '').toString(),
          job_endpoint: jobEndpoint,
          payload,
          kind:
            scheduleMode === 'scheduled' ? 'once_at'
            : scheduleMode === 'delayed' ? 'once_after'
            : 'cron',
        };
        if (scheduleMode === 'scheduled') {
          scheduleBody.fire_at = (values.runAt as Dayjs).toISOString();
        } else if (scheduleMode === 'delayed') {
          scheduleBody.delay_value = Number(values.delayValue);
          scheduleBody.delay_unit = (values.delayUnit as ScheduleDelayUnit) || 'minutes';
        } else {
          scheduleBody.cron_expr = (values.cronExpr || '').trim();
          scheduleBody.cron_tz = 'Asia/Shanghai';
        }
        await createSchedule(scheduleBody);
        message.success('定时已创建，到点将自动触发');
        setCopyDefaults(null);
        setEnableSchedule(false);
        setScheduleMode('scheduled');
        setCronPreview(null);
        createForm.resetFields();
        onClose();
        onSuccess?.();
        return;
      }

      if (buildType === 'package_bazel' || buildType === 'daily') {
        const baseVersion = (values.baseVersion || '').trim();
        if (!baseVersion) {
          message.error('请输入基线版本');
          setCreateLoading(false);
          return;
        }
        const bazelParams: CreateBazelJobParams = {
          taskTypeId: effectiveTaskTypeId,
          jobName: values.jobName || undefined,
          priority: values.priority !== undefined ? Number(values.priority) : undefined,
          ...sceneSourcePayload,
          baseVersion,
          versionDescription:
            (values.versionDescription || '').trim() || `${PR_DATA_TASK_TYPE_NAME}仿真`,
          changeModule: (values.changeModule || '').trim(),
          branchDpInfo: normalizeBranchDpInfoForApi(values.branchDpInfo || ''),
          ...predictionVersionPayload,
          ...(customParams ? { customParams } : {}),
          ...(repeatTimes > 1 ? { repeatTimes } : {}),
          ...(buildType === 'package_bazel' && bazelPresetChoice !== 'custom'
            ? { presetKey: bazelPresetChoice } : {}),
        };
        if (dryRunMode) {
          const dryParams = {
            ...(bazelParams as unknown as Record<string, unknown>),
            baseVersion: versionPlaceholder,
          };
          onDryRunResult?.({ endpoint: 'bazel_create', params: dryParams });
          setCopyDefaults(null);
          createForm.resetFields();
          onClose();
          return;
        }
        const result = await createBazelJob(bazelParams);
        message.success(
          `已发起 bazel 编译${repeatSuffix}（版本 ${result.version}），编译完成后自动调度`,
        );
      } else {
        let resolvedBuildType: BuildType;
        let buildSpec: BuildSpec;
        if (buildType === 'precompiled') {
          if (precompiledSubtype === 's3') {
            const bosUrl = (values.s3PackageUrl || '').trim();
            if (!/^bos:\/\//i.test(bosUrl)) {
              message.error('S3 地址必须以 bos:// 开头');
              setCreateLoading(false);
              return;
            }
            resolvedBuildType = 'package_neodata';
            buildSpec = { type: 'package_neodata', algorithmVersion: bosUrl };
          } else {
            const ver = (values.algorithmVersion || '').trim();
            if (!ver) {
              message.error('请输入版本号');
              setCreateLoading(false);
              return;
            }
            resolvedBuildType = 'package_neodata';
            buildSpec = { type: 'package_neodata', algorithmVersion: ver };
          }
        } else {
          message.error(`未知的 buildType=${String(buildType)},请刷新页面重试`);
          setCreateLoading(false);
          return;
        }
        const params: CreateJobParams = {
          priority: Number(values.priority),
          taskTypeId: effectiveTaskTypeId,
          jobName: values.jobName || undefined,
          ...sceneSourcePayload,
          buildType: resolvedBuildType,
          buildSpec,
          ...predictionVersionPayload,
          ...(customParams ? { customParams } : {}),
          ...(repeatTimes > 1 ? { repeatTimes } : {}),
        };
        if (dryRunMode) {
          const dryBuildSpec = {
            ...(buildSpec as unknown as Record<string, unknown>),
            algorithmVersion: versionPlaceholder,
          };
          const dryParams = {
            ...(params as unknown as Record<string, unknown>),
            buildSpec: dryBuildSpec,
          };
          onDryRunResult?.({ endpoint: 'create', params: dryParams });
          setCopyDefaults(null);
          createForm.resetFields();
          onClose();
          return;
        }
        await createJob(params);
        message.success(`Job 创建成功${repeatSuffix}`);
      }
      setCopyDefaults(null);
      createForm.resetFields();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      if (err?.errorFields) {
        if (err.errorFields.some((field: { name: (string | number)[] }) => field.name[0] === 'customParameterEntries')) {
          setAdvancedOpen(true);
        }
        return; // 表单校验错误
      }
      console.error('创建任务失败:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Modal
      title="创建数据更新 Job"
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>取消</Button>,
        <Button key="submit" type="primary" loading={createLoading} onClick={handleSubmit}>
          创建 Job
        </Button>,
      ]}
      width={600}
      destroyOnHidden
    >
      <Form
        form={createForm}
        layout="vertical"
        style={{ marginTop: 20 }}
        onValuesChange={(changed) => {
          if ('sceneNames' in changed && copiedSceneIds) {
            setCopiedSceneIds(null);
          }
        }}
      >
        {enableSchedule && (
          <Alert
            type="warning"
            showIcon
            message="开启定时功能后将不在创建过程检查包以及分支是否正确，只会在运行时直接报错，请自行单次触发确保参数正确"
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 任务类型固定 PR_DATA：只读展示 + 隐藏 Form.Item 承载真实 taskTypeId。 */}
        <Form.Item label="任务类型" required>
          <Input value={PR_DATA_TASK_TYPE_LABEL} disabled />
        </Form.Item>
        <Form.Item name="taskTypeId" hidden rules={[{ required: true }]}>
          <InputNumber />
        </Form.Item>

        <Form.Item
          label="创建类型"
          required
          tooltip={
            <div style={{ whiteSpace: 'pre-line' }}>
              {`使用编译好的版本：${BUILD_TYPE_TOOLTIPS.precompiled}\n基于已有版本编译：${BUILD_TYPE_TOOLTIPS.package_bazel}\n基于 daily 版本：${BUILD_TYPE_TOOLTIPS.daily}`}
            </div>
          }
        >
          <Radio.Group
            value={buildType}
            onChange={(e) => {
              const next = e.target.value as UiBuildType;
              setBuildType(next);
              if (next !== 'daily') {
                setDailyBaseVersionCandidates([]);
                setDailyTagSelected(false);
              }
              if (copyDefaults) {
                if (next === 'package_bazel' || next === 'daily') {
                  createForm.setFieldsValue({
                    baseVersion: copyDefaults.baseVersion,
                    versionDescription: copyDefaults.versionDescription || undefined,
                    changeModule: copyDefaults.changeModule,
                    branchDpInfo: copyDefaults.branchDpInfo,
                    algorithmVersion: undefined,
                    branchCommitInput: undefined,
                    s3PackageUrl: undefined,
                  });
                } else if (next === 'precompiled') {
                  const full = (copyDefaults.fullPackageUrl || '').trim();
                  const isBos = /^bos:\/\//i.test(full);
                  setPrecompiledSubtype(isBos ? 's3' : 'version');
                  createForm.setFieldsValue({
                    algorithmVersion: isBos ? undefined : copyDefaults.algorithmVersion,
                    s3PackageUrl: isBos ? full : undefined,
                    branchCommitInput: undefined,
                  });
                }
              } else {
                createForm.setFieldsValue({
                  algorithmVersion: undefined,
                  branchCommitInput: undefined,
                  baseVersion: undefined,
                  changeModule: undefined,
                  branchDpInfo: undefined,
                  versionDescription: undefined,
                  s3PackageUrl: undefined,
                });
              }
            }}
          >
            {BUILD_TYPE_ORDER.map((bt) => (
              <Tooltip key={bt} title={BUILD_TYPE_TOOLTIPS[bt]} placement="top">
                <Radio
                  value={bt}
                  style={
                    bt === 'package_bazel'
                      ? { color: 'rgba(0, 0, 0, 0.35)' }
                      : undefined
                  }
                >
                  {BUILD_TYPE_LABELS[bt]}
                </Radio>
              </Tooltip>
            ))}
          </Radio.Group>
        </Form.Item>

        {buildType === 'precompiled' && (
          <>
            {/* 子选项卡：版本号 / S3 地址 / 使用 daily 版本 —— 提交时都走后端 package_neodata */}
            <Form.Item label="输入方式" tooltip={BUILD_TYPE_TOOLTIPS.precompiled}>
              <Segmented
                value={precompiledSubtype}
                onChange={(v) => {
                  const next = v as PrecompiledSubtype;
                  const prev = precompiledSubtype;
                  setPrecompiledSubtype(next);
                  if (next === 's3') {
                    createForm.setFieldsValue({ algorithmVersion: undefined });
                  }
                  if (prev === 's3') {
                    createForm.setFieldsValue({ s3PackageUrl: undefined });
                  }
                  if (prev === 'daily' && next !== 'daily') {
                    setPrecompiledDailyTagSelected(false);
                    setPrecompiledDailyCandidates([]);
                  }
                }}
                options={[
                  { label: PRECOMPILED_SUBTYPE_LABELS.version, value: 'version' },
                  { label: PRECOMPILED_SUBTYPE_LABELS.s3, value: 's3' },
                  { label: PRECOMPILED_SUBTYPE_LABELS.daily, value: 'daily' },
                ]}
              />
            </Form.Item>

            {precompiledSubtype === 'daily' && (
              <>
                <DailyTagPicker
                  onPick={(versionNo: string) => {
                    createForm.setFieldsValue({ algorithmVersion: versionNo });
                  }}
                  onVersionsLoaded={setPrecompiledDailyCandidates}
                  onTagChange={(tag) => {
                    setPrecompiledDailyTagSelected(!!tag);
                    if (!tag) {
                      createForm.setFieldsValue({ algorithmVersion: undefined });
                    }
                  }}
                />
              </>
            )}

            {(precompiledSubtype === 'version' || precompiledSubtype === 'daily') && (
              <Form.Item
                name="algorithmVersion"
                label="版本号"
                rules={[{ required: true, message: '请输入版本号' }]}
                tooltip="填写已发布的算法包版本号（如 x86.20260430.release.10.8.3），后端按模板拼装 OBS 地址。"
                extra={renderProbeHint(algoVersionProbe)}
              >
                {precompiledSubtype === 'daily' ? (
                  <DailyBaseVersionPicker
                    candidates={precompiledDailyCandidates}
                    tagSelected={precompiledDailyTagSelected}
                    placeholder="选 Tag 后自动填入最新;点右侧查看历史或直接输入"
                    suffix={renderProbeSuffix(algoVersionProbe)}
                  />
                ) : (
                  <Input
                    placeholder="请输入版本号（如 x86.20260430.release.10.8.3）"
                    suffix={renderProbeSuffix(algoVersionProbe)}
                    allowClear
                  />
                )}
              </Form.Item>
            )}

            {precompiledSubtype === 's3' && (
              <Form.Item
                name="s3PackageUrl"
                label="S3 地址"
                tooltip="导入百度云的包，仅支持 bos:// 开头的地址。"
                rules={[
                  { required: true, message: '请输入 S3 地址' },
                  {
                    validator: (_, value) => {
                      const v = (value || '').trim();
                      if (!v) return Promise.resolve();
                      if (!/^bos:\/\//i.test(v)) {
                        return Promise.reject(new Error('S3 地址必须以 bos:// 开头（仅支持百度云）'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={
                  <>
                    <span style={{ fontSize: 12, color: '#999' }}>导入百度云的包，仅支持 bos:// 开头的地址</span>
                    {renderProbeHint(s3PackageUrlProbe) && (
                      <>
                        <br />
                        {renderProbeHint(s3PackageUrlProbe)}
                      </>
                    )}
                  </>
                }
              >
                <Input
                  placeholder="bos://bucket/path/to/package.tgz"
                  suffix={renderProbeSuffix(s3PackageUrlProbe)}
                  allowClear
                />
              </Form.Item>
            )}
          </>
        )}

        {(buildType === 'package_bazel' || buildType === 'daily') && (
          <>
            {buildType === 'daily' && (
              <DailyTagPicker
                onPick={(baseVersion: string) => {
                  createForm.setFieldsValue({ baseVersion });
                }}
                onVersionsLoaded={setDailyBaseVersionCandidates}
                onTagChange={(tag) => {
                  setDailyTagSelected(!!tag);
                  if (!tag) {
                    createForm.setFieldsValue({ baseVersion: undefined });
                  }
                }}
              />
            )}
            {buildType === 'package_bazel' && (
              <Form.Item
                label="一键填版本"
                tooltip="选择任一预设，把对应版本的 neolix-repo / cyber / runtime / tools / dreamview / simulation 分支组合与默认基线版本合并到「分支信息」（同名仓库以预设为准、其余保留）；「自定义版本」不主动填充，但保留你已填的内容。预设由管理员在「系统 - 系统参数运维」页维护。"
              >
                <Select
                  value={bazelPresetChoice}
                  style={{ width: 200 }}
                  onSelect={(v: 'custom' | string) => {
                    setBazelPresetChoice(v);
                    if (v !== 'custom') {
                      applyBazelPreset(v);
                    }
                  }}
                  options={[
                    { label: '自定义版本', value: 'custom' },
                    ...Object.entries(bazelPresets).map(([key, p]) => ({
                      label: p.label || key,
                      value: key,
                    })),
                  ]}
                />
                <span style={{ marginLeft: 8, fontSize: 12, color: '#9b9da3' }}>
                  选预设会合并到下方「分支信息」（同名仓库以预设为准、其余保留），并填入「基线版本」
                </span>
              </Form.Item>
            )}

            <Form.Item
              name="baseVersion"
              label="基线版本"
              rules={[
                { required: true, whitespace: true, message: '请输入基线版本' },
              ]}
              tooltip="本次编译所基于的基线版本（base_version，必填）；编译产物版本号由后端按「日期.simulation.{环境}.{用户ID}.序号」自动生成。"
              extra={
                <>
                  <span style={{ color: '#9b9da3', fontSize: 12 }}>
                    必填，建议填写具体基线 tag（如 20260609.release.10.10.8）。
                  </span>
                  {renderProbeHint(baseVersionProbe) && (
                    <>
                      <br />
                      {renderProbeHint(baseVersionProbe)}
                    </>
                  )}
                </>
              }
            >
              {buildType === 'daily' ? (
                <DailyBaseVersionPicker
                  candidates={dailyBaseVersionCandidates}
                  tagSelected={dailyTagSelected}
                  placeholder="选 Tag 后自动填入最新;点右侧查看历史或直接输入"
                  suffix={renderProbeSuffix(baseVersionProbe)}
                />
              ) : (
                <Input
                  placeholder="如 20260609.release.10.10.8"
                  suffix={renderProbeSuffix(baseVersionProbe)}
                  allowClear
                />
              )}
            </Form.Item>

            <Form.Item
              name="branchDpInfo"
              label="分支信息"
              extra={
                <span style={{ fontSize: 12, color: '#999' }}>
                  每行一对，格式：<code>module branch</code>（空格分隔）；
                  也兼容老格式 <code>module:branch</code>。提交时自动规整为
                  <code>module:branch,...</code> 发给后端。
                  <br />
                  分支位置也可直接填 <code>40 位 commitId</code>，
                  系统识别为 commit 直接使用、不再解析分支；<strong>短 commit 不支持</strong>。
                  <br />
                  仓库不变（无分支改动）则留空
                </span>
              }
              getValueFromEvent={(e) => {
                const v = e.target.value;
                createForm.setFieldsValue({ changeModule: deriveChangeModule(v) });
                return v;
              }}
            >
              <Input.TextArea
                placeholder={`planning orin_dev\ncontrol db31cf58ef237a576eab0d6e407b18fc80d29286`}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
            </Form.Item>

            {/* changeModule / versionDescription 作为隐藏项存在，提交时读取。 */}
            <Form.Item name="changeModule" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="versionDescription" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        {/* 模型主版本：与 Job 名称同级的顶层字段，渲染在其上方。这是决定本次仿真跑哪一版
            模型的关键参数，原先埋在「高级选项 · 自定义参数」里要连点三层才看得到，容易被漏掉。 */}
        <ModelReleaseField
          form={createForm}
          taskTypeId={prDataTaskTypeId ?? undefined}
          active={open}
          definitions={customParameterDefinitions.definitions}
          loading={customParameterDefinitions.loading}
          error={customParameterDefinitions.error}
          onReload={customParameterDefinitions.reload}
          loadRelease={customParameterDefinitions.loadRelease}
        />

        <Form.Item
          name="jobName"
          label="Job 名称"
          rules={[{ required: true, message: '请输入 Job 名称' }]}
        >
          <Input placeholder="请输入 Job 名称" />
        </Form.Item>

        {/* 预测数据版本（必填，**完整串**）：从上方「版本号 / 基线版本」自动提取并补
            prediction. 前缀（x86.…release.AD3.1.3→prediction.AD3.1.3、
            …daily.sim.orin_dev→prediction.orin_dev，时间戳由后端在产出时追加）。
            下拉里选已有版本 = 补进该版本（可选已有场景「跳过 / 覆盖」）；
            手填新串 = 以该串新建版本。 */ }
        <Form.Item
          name="predictionVersion"
          label="预测数据版本"
          rules={[{ required: true, whitespace: true, message: '请填写预测数据版本' }]}
          tooltip="本次预测数据产出的**完整版本号**（prediction.xxx）。从上方版本号 / 基线版本自动提取；下拉里选已有版本 = 把这次产出补进该版本，手填新串 = 以该串新建版本。补进已有版本时可选择已有场景「跳过」（默认，保留旧数据只补缺）或「覆盖」（重新预测并替换旧数据）。"
          extra={
            <div style={{ fontSize: 12, color: '#999', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {trimmedPredictionVersion && versionExists ? (
                <span>
                  <Tag color="blue" style={{ marginInlineEnd: 6 }}>已存在</Tag>
                  已覆盖 {selectedVersionMeta?.coveredScenes ?? 0}
                  {selectedVersionMeta?.sceneTotal ? `/${selectedVersionMeta.sceneTotal}` : ''} 个场景
                  {selectedVersionMeta?.isCurrent ? ' · 当前生效版本' : ''}
                </span>
              ) : trimmedPredictionVersion ? (
                <span>
                  {selectedVersionMeta ? '目录已登记的版本，尚无产出' : '新版本号'}
                  {' → 将以该版本号新建'}
                </span>
              ) : (
                <span>
                  从上方版本号 / 基线版本自动提取；也可下拉选已有版本或直接填写，如
                  {' '}prediction.AD3.1.3 / prediction.orin_dev
                </span>
              )}
              {versionExists && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>已有场景</span>
                  <Segmented
                    size="small"
                    value={fillMode}
                    onChange={(v) => setFillMode(v as 'skip' | 'overwrite')}
                    options={[
                      { label: '跳过（默认）', value: 'skip' },
                      { label: '覆盖', value: 'overwrite' },
                    ]}
                  />
                  <span style={{ color: fillMode === 'overwrite' ? '#d46b08' : '#999' }}>
                    {fillMode === 'overwrite'
                      ? '重新预测并覆盖该版本内的旧数据'
                      : '保留旧数据，只补缺失的场景'}
                  </span>
                </div>
              )}
            </div>
          }
        >
          <AutoComplete
            style={{ width: '100%' }}
            notFoundContent={fillVersionLoading ? '加载中…' : undefined}
            placeholder="如 prediction.AD3.1.3 / prediction.release.10.12.34 / prediction.orin_dev"
            onBlur={normalizePredictionVersionInput}
            filterOption={(inputValue, option) =>
              String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase())
            }
            options={fillVersionOptions.map((v) => ({
              value: v.predictionVersion,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace' }}>{v.predictionVersion}</span>
                  <span
                    style={{
                      marginLeft: 'auto', fontSize: 11, color: '#8c9099',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {v.hasHeader
                      ? `${v.isCurrent ? '当前生效 · ' : ''}已覆盖 ${v.coveredScenes ?? 0}${v.sceneTotal ? `/${v.sceneTotal}` : ''}`
                      : '尚无产出'}
                  </span>
                </div>
              ),
            }))}
          />
        </Form.Item>

        {/* 场景来源：「按场景集合 / 按场景 / 按任务查询」三选一。 */}
        <Form.Item label="场景来源" required>
          <Segmented
            value={sceneTab}
            onChange={(v) => setSceneTab(v as 'sceneSet' | 'scene' | 'byJob')}
            options={[
              { label: '按场景集合', value: 'sceneSet' },
              { label: '按场景', value: 'scene' },
              ...(byTaskQuery
                ? [{ label: '按任务查询', value: 'byJob' }]
                : []),
            ]}
          />
        </Form.Item>

        {sceneTab === 'sceneSet' && stagedGroupTokens.length > 0 && (
          <Form.Item label="从场景管理带入的分组">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {stagedGroupTokens.map((tok) => (
                <Tag
                  key={tok}
                  closable
                  color="processing"
                  onClose={() => setStagedGroupTokens(stagedGroupTokens.filter((t) => t !== tok))}
                >
                  {presetSceneSetGroupLabels?.[tok] || tok}
                </Tag>
              ))}
            </div>
          </Form.Item>
        )}

        {sceneTab === 'sceneSet' && (
          <Form.Item
            name="sceneSetIds"
            label="场景列表"
            required
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const hasTree = Array.isArray(value) && value.length > 0;
                  const hasGroup = stagedGroupTokens.length > 0;
                  return hasTree || hasGroup
                    ? Promise.resolve()
                    : Promise.reject(new Error('请至少选择一个场景集合'));
                },
              },
            ]}
          >
            <TreeSelect
              treeData={convertSceneTreeToTreeData(sceneTree)}
              placeholder="请选择场景集合（可多选）"
              multiple
              treeCheckable
              showCheckedStrategy="SHOW_PARENT"
              showSearch
              filterTreeNode={(inputValue, treeNode) => {
                const title = treeNode?.title as string;
                return title?.toLowerCase().includes(inputValue.toLowerCase());
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {sceneTab === 'scene' && (
          <Form.Item
            name="sceneNames"
            label="场景名称"
            required
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const names = Array.isArray(value)
                    ? value.map((n: string) => (n || '').trim()).filter(Boolean)
                    : [];
                  if (names.length === 0) {
                    return Promise.reject(new Error('请输入场景名称'));
                  }
                  if (names.length > MAX_SCENE_IDS_PER_JOB) {
                    return Promise.reject(
                      new Error(`最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${names.length} 个`),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={
              <span style={{ fontSize: 12, color: '#999' }}>
                输入场景名称后回车确认，可输入多个（支持逗号 / 空格 / 换行分隔粘贴），
                上限 {MAX_SCENE_IDS_PER_JOB} 个
              </span>
            }
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',', '，', ' ', ';', '；', '\n', '\t']}
              placeholder="请输入场景名称，如 LPL0000291017"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {/* 按任务查询:场景池已由调用方冻结,弹窗内只做只读展示 + 修改筛选回退链接。 */}
        {sceneTab === 'byJob' && byTaskQuery && (
          <Form.Item
            label="场景池"
            extra={
              <a
                onClick={onClose}
                style={{ fontSize: 12, color: '#1677ff' }}
              >
                {byTaskQuery.sceneIds && byTaskQuery.sceneIds.length > 0
                  ? '← 返回 task 列表重新勾选'
                  : '← 修改筛选(回到 task 列表页重新查询)'}
              </a>
            }
          >
            <div
              style={{
                padding: '10px 12px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 13,
              }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <span style={{ color: '#8c8c8c' }}>源 Job</span>
                <span style={{
                  fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace',
                  color: '#1f1f1f',
                }}>
                  #{byTaskQuery.jobId}
                </span>
                {byTaskQuery.jobName ? (
                  <span style={{
                    color: '#595959',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {byTaskQuery.jobName}
                  </span>
                ) : null}
                {typeof byTaskQuery.sceneCount === 'number' ? (
                  <span style={{ marginLeft: 'auto' }}>
                    <span style={{ color: '#8c8c8c', marginRight: 4 }}>将发起</span>
                    <strong style={{
                      color: '#1677ff',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {byTaskQuery.sceneCount}
                    </strong>
                    <span style={{ color: '#8c8c8c', marginLeft: 4 }}>个场景</span>
                  </span>
                ) : null}
              </div>
              {byTaskQuery.sceneIds && byTaskQuery.sceneIds.length > 0 ? (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  <li style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: '#8c8c8c', minWidth: 96, flexShrink: 0 }}>勾选任务</span>
                    <span style={{ color: '#1f1f1f' }}>
                      已手动勾选 {byTaskQuery.sceneIds.length} 个场景
                    </span>
                  </li>
                </ul>
              ) : byTaskQuery.filterEntries && byTaskQuery.filterEntries.length > 0 ? (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {byTaskQuery.filterEntries.map((f) => (
                    <li
                      key={f.key}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 12,
                        padding: '4px 0',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: '#8c8c8c', minWidth: 96, flexShrink: 0 }}>
                        {f.label}
                      </span>
                      <span style={{ color: '#1f1f1f', flex: 1, wordBreak: 'break-all' }}>
                        {f.valueLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: '#bfbfbf', fontSize: 12 }}>
                  未应用任何筛选 —— 会用该 Job 的全部场景发起任务
                </div>
              )}
            </div>
          </Form.Item>
        )}

        {/* 高级选项：默认收起。放「自定义参数」「优先级」「重复运行次数」与「开启定时」开关。
            自定义参数用 forceRender + 受控 activeKey 保证收起时复制值/提交校验仍生效。 */}
        <Collapse
          ghost
          size="small"
          activeKey={advancedOpen ? ['advanced'] : []}
          onChange={keys => setAdvancedOpen(keys.includes('advanced'))}
          items={[{
            key: 'advanced',
            forceRender: true,
            label: <span style={{ fontSize: 13, color: '#5e6ad2' }}>高级选项</span>,
            children: (
              <>
                <CustomParametersFields
                  form={createForm}
                  taskTypeId={prDataTaskTypeId ?? undefined}
                  active={open}
                  definitions={customParameterDefinitions.definitions}
                  loading={customParameterDefinitions.loading}
                  error={customParameterDefinitions.error}
                  onReload={customParameterDefinitions.reload}
                  promotedKeys={PROMOTED_CUSTOM_PARAMETER_KEYS}
                />
                <Form.Item
                  name="priority"
                  label="优先级"
                  initialValue={6}
                  tooltip="调度优先级。默认「中」;高/紧急建议少用,避免占用他人配额。"
                  style={{ marginBottom: 16 }}
                >
                  <Select
                    style={{ width: 200 }}
                    options={priorityList.map((p) => ({
                      label: p.label ?? p.name ?? String(p.value),
                      value: Number(p.value ?? p.code),
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="repeatTimes"
                  label="重复运行次数"
                  initialValue={1}
                  tooltip="同一份参数循环创建 N 个 Job（1-10）；N>1 时 jobName 自动追加 _#i/N 序号方便检索同一批次。"
                  rules={[{ type: 'integer', min: 1, max: 10, message: '请输入 1-10 之间的整数' }]}
                  style={{ marginBottom: 16 }}
                >
                  <InputNumber min={1} max={10} precision={0} style={{ width: 120 }} />
                </Form.Item>

                {/* 开启定时/延迟/循环创建 Job（docs/schedule-v3.md §5.1）。 */}
                <Form.Item label="开启定时" tooltip="开关开后本次提交将不立即建 Job，而是写一条定时记录，到点由后台协程同进程 await 对应异步 service 方法。分支/包错误到点才会暴露，请自行单次触发确保参数正确。">
                  <Space>
                    <Switch
                      checked={enableSchedule}
                      onChange={(checked) => {
                        setEnableSchedule(checked);
                        if (!checked) {
                          createForm.setFieldsValue({
                            runAt: undefined,
                            delayValue: undefined,
                            delayUnit: 'minutes',
                            cronExpr: undefined,
                          });
                          setCronPreview(null);
                        } else if (!createForm.getFieldValue('delayUnit')) {
                          createForm.setFieldsValue({ delayUnit: 'minutes' });
                        }
                      }}
                    />
                    <span style={{ color: '#9b9da3', fontSize: 12 }}>
                      {enableSchedule ? '本次将不立即建 Job，到点自动触发' : '默认关闭，立即建 Job'}
                    </span>
                  </Space>
                </Form.Item>

                {enableSchedule && (
                  <Form.Item label="触发方式" required>
                    <Radio.Group
                      value={scheduleMode}
                      onChange={(e) => {
                        const next = e.target.value as 'scheduled' | 'delayed' | 'cron';
                        setScheduleMode(next);
                        createForm.setFieldsValue({
                          runAt: undefined,
                          delayValue: undefined,
                          delayUnit: 'minutes',
                          cronExpr: undefined,
                        });
                        setCronPreview(null);
                      }}
                    >
                      <Radio value="scheduled">定时</Radio>
                      <Radio value="delayed">延时</Radio>
                      <Radio value="cron">循环 (cron)</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'scheduled' && (
                  <Form.Item
                    name="runAt"
                    label="定时触发时间"
                    rules={[
                      { required: true, message: '请选择触发时间' },
                      {
                        validator: (_, value: Dayjs | undefined) => {
                          if (!value) return Promise.resolve();
                          if (value.toDate().getTime() < Date.now() + 60_000) {
                            return Promise.reject(new Error('触发时间不得早于当前时间 + 1 分钟'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    extra={
                      <span style={{ fontSize: 12, color: '#999' }}>
                        到点后自动建 Job；时区按本地浏览器处理。
                      </span>
                    }
                  >
                    <DatePicker
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      style={{ width: '100%' }}
                      placeholder="选择触发时间"
                      disabledDate={(current) => {
                        return current ? current.endOf('day') < dayjs().startOf('day') : false;
                      }}
                    />
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'delayed' && (
                  <Form.Item label="延时触发" required>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item name="delayValue" noStyle rules={[{ required: true, message: '请输入延时数值' }]}>
                        <InputNumber min={1} precision={0} placeholder="数值" style={{ width: '60%' }} />
                      </Form.Item>
                      <Form.Item name="delayUnit" noStyle initialValue="minutes">
                        <Select style={{ width: '40%' }} options={[
                          { label: '分钟', value: 'minutes' },
                          { label: '小时', value: 'hours' },
                          { label: '天', value: 'days' },
                        ]} />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'cron' && (
                  <Form.Item
                    name="cronExpr"
                    label="Cron 表达式"
                    rules={[
                      { required: true, message: '请输入 cron 表达式' },
                    ]}
                    extra={
                      <span style={{ fontSize: 12, color: '#999' }}>
                        5 段标准 cron（分 时 日 月 周）；最小间隔 ≥5 分钟。例如 <code>0 9 * * 1</code> = 每周一 09:00。
                      </span>
                    }
                  >
                    <Input placeholder="0 9 * * 1" />
                  </Form.Item>
                )}

                {enableSchedule && scheduleMode === 'cron' && (
                  <div style={{ marginBottom: 16, marginTop: -8 }}>
                    <Space>
                      <Button
                        size="small"
                        onClick={async () => {
                          const expr = (createForm.getFieldValue('cronExpr') || '').toString().trim();
                          if (!expr) {
                            message.warning('请先填写 cron 表达式');
                            return;
                          }
                          setCronPreviewLoading(true);
                          try {
                            const r = await previewCron(expr, 'Asia/Shanghai', 3);
                            setCronPreview(r.times);
                          } catch (err) {
                            console.error('cron 预演失败:', err);
                          } finally {
                            setCronPreviewLoading(false);
                          }
                        }}
                        loading={cronPreviewLoading}
                      >
                        预演未来 3 次
                      </Button>
                      {cronPreview && cronPreview.length > 0 && (
                        <span style={{ fontSize: 12, color: '#16a34a' }}>
                          下次触发：{dayjs(cronPreview[0]).format('YYYY-MM-DD HH:mm')}；
                          之后：{cronPreview.slice(1).map(t => dayjs(t).format('MM-DD HH:mm')).join(' / ')}
                        </span>
                      )}
                    </Space>
                  </div>
                )}
              </>
            ),
          }]}
        />
      </Form>
    </Modal>
  );
}
